/**
 * ChangelogDetailDialog — nested modal showing the full markdown `details` of a
 * changelog change, opened from the "Show more info…" link in ChangelogContent.
 *
 * Renders the markdown with the same react-markdown + remark-gfm + rehype-sanitize
 * pipeline used elsewhere in the app; links open in the system browser. Footer
 * link-buttons jump to the docs page / blog post when the change provides them.
 */

import { useCallback } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { BookOpen, Newspaper } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { openExternalUrl } from '../../help-feedback';
import type { ChangelogChange } from '../data/changelog';
import { changeText } from '../data/changelog';

export type ChangelogLinkTarget = 'docs' | 'blog';

interface ChangelogDetailDialogProps {
  /** The change whose details to show, or null when closed. */
  change: ChangelogChange | null;
  onClose: () => void;
  /** Fired when a footer docs/blog link is clicked (for analytics). */
  onLinkClick?: (target: ChangelogLinkTarget) => void;
}

export function ChangelogDetailDialog({ change, onClose, onLinkClick }: ChangelogDetailDialogProps) {
  const handleBodyLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    if (href) openExternalUrl(href);
  }, []);

  const handleFooterLink = useCallback(
    (url: string, target: ChangelogLinkTarget) => {
      onLinkClick?.(target);
      openExternalUrl(url);
    },
    [onLinkClick],
  );

  const open = change !== null && !!change.details;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-lg p-0 gap-0" data-testid="changelog-detail-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-base leading-snug">
            {change ? changeText(change) : ''}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] px-6 py-2">
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <Markdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={{
                a: ({ href, children, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
                  <a
                    {...rest}
                    href={href}
                    onClick={handleBodyLinkClick}
                    className="text-primary underline cursor-pointer"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {change?.details ?? ''}
            </Markdown>
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 pb-6 pt-3 border-t border-border sm:justify-start gap-2">
          {change?.docsUrl && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => handleFooterLink(change.docsUrl as string, 'docs')}
              data-testid="changelog-detail-docs-link"
            >
              <BookOpen className="h-3.5 w-3.5" />
              Read the docs
            </Button>
          )}
          {change?.blogUrl && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => handleFooterLink(change.blogUrl as string, 'blog')}
              data-testid="changelog-detail-blog-link"
            >
              <Newspaper className="h-3.5 w-3.5" />
              Read the blog post
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
